import { EllipsisOutlined, MenuOutlined } from '@ant-design/icons';
import { Drawer, Grid, Menu, Typography } from 'antd';
import { ItemType, SubMenuType } from 'antd/es/menu/interface';
import React, { useEffect } from 'react';
import { NavLink, useLocation, useMatches } from 'react-router-dom';
import { DecafMenuItem } from '../types';

function getMenuItemKey(item: DecafMenuItem, isChild = false): string {
  if (!item) return '';
  if ('to' in item && item.to) {
    const parentKeyPrefix = item.to.substring(0, item.to.lastIndexOf('/')) || item.to;
    const parentKey = parentKeyPrefix + ':_parent_';
    return isChild ? parentKey : item.to;
  }
  if ('href' in item && item.href) return item.href + (isChild ? ':_parent_' : '');
  if ('children' in item && item.children) return getMenuItemKey(item.children[0], true);
  return '';
}

function buildAntMenuFromDecafMenu(routes: DecafMenuItem[]): ItemType[] {
  const result: ItemType[] = [];

  routes.forEach((route) => {
    const item: ItemType = {
      key: getMenuItemKey(route),
      label: route.label,
      icon: route.icon,
    };

    if ('to' in route && route.to) {
      item.label = (
        <NavLink to={route.to} end={route.to === '/'}>
          {route.label}
        </NavLink>
      );
    } else if ('href' in route && route.href) {
      item.label = <a href={route.href}>{route.label}</a>;
    } else if (route.children) {
      item.label = route.label;
      (item as unknown as SubMenuType).children = buildAntMenuFromDecafMenu(route.children || []);
    }

    result.push(item);
  });

  return result;
}

export function DecafPoweredBy() {
  return (
    <Typography.Text type="secondary">
      Powered by{' '}
      <a href="https://teloscube.com" target="_blank" rel="noreferrer">
        Teloscube
      </a>
    </Typography.Text>
  );
}

export interface MenuProps {
  title: string;
  menu: DecafMenuItem[];
}

export default function DecafMenu(props: MenuProps) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const matches = useMatches();
  const location = useLocation();
  const { md } = Grid.useBreakpoint();

  const menuItems = buildAntMenuFromDecafMenu(props.menu);
  const matchedMenuItems = matches
    .map((match) => match.pathname)
    .filter((pathname) => (location.pathname !== '/' ? pathname !== '/' : true)); // WORKAROUND: the root path always matches. We don't want that.

  useEffect(() => {
    if (!md) {
      setDrawerOpen(false);
    }
  }, [location.pathname, md]);

  const menu = (
    <Menu
      items={menuItems}
      selectedKeys={matchedMenuItems}
      mode={md ? 'horizontal' : 'inline'}
      overflowedIndicator={<EllipsisOutlined style={{ fontSize: 20, color: 'white' }} />}
      style={{ flex: 'auto', justifyContent: 'flex-end', backgroundColor: 'transparent', border: 'none', minWidth: 0 }}
    />
  );

  if (md) {
    return menu;
  }

  return (
    <div style={{ display: 'flex', flex: 'auto', justifyContent: 'flex-end' }}>
      <MenuOutlined style={{ fontSize: 20, marginRight: 20 }} onClick={() => setDrawerOpen(!drawerOpen)} />

      <Drawer
        open={drawerOpen}
        footer={<DecafPoweredBy />}
        onClose={() => setDrawerOpen(false)}
        styles={{ body: { padding: 10 } }}
        size={document.body.clientWidth - 50 > 350 ? 350 : document.body.clientWidth * 0.85}
      >
        {menu}
      </Drawer>
    </div>
  );
}
